import { Component, computed, HostListener, inject, OnInit, signal } from '@angular/core';
import { SubscriptionService } from '../../services/subscription/subscription.service';
import { ChoosePlanService } from '../choose-plan/service/choose-plan/choose-plan.service';
import { CurrencyPipe } from '@angular/common';
import { ViewportOverlayDirective } from '../../common/directives/viewport-overlay/viewport-overlay.directive';
import { CreateSubSessionMultiProduct, SubscriptionEnum } from '../../common/general-types/subscription.dto';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { finalize, switchMap, take, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';

@Component({
  selector: 'app-choose-plan-bolt-on-add',
  standalone: true,
  imports: [CurrencyPipe],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './choose-plan-bolt-on-add.component.html',
  styleUrl: './choose-plan-bolt-on-add.component.css'
})
export class ChoosePlanBoltOnAddComponent implements OnInit {

  subscriptionLoadingCtrl = signal<boolean>(false);

  showAllBoltOnDetails = signal(false);

  totalBoltOnsMonthly = computed(() => {
    const plan = this.choosePlanService.subscriptionPlans[0];
    const planPrice = plan ? plan.price : 0;
    const boltOnsPrice = Array.from(this.subscriptionService.selectedBoltOns()).reduce((sum, id) => {
      const boltOn = this.choosePlanService.boltOns.find(b => b.stripe_id === id);
      return sum + (boltOn ? boltOn.price : 0);
    }, 0);
    return Math.round(boltOnsPrice);
  });

  totalMonthly = computed(() => {
    const plan = this.choosePlanService.subscriptionPlans[0];
    const planPrice = plan ? plan.price : 0;
    const boltOnsPrice = Array.from(this.subscriptionService.selectedBoltOns()).reduce((sum, id) => {
      const boltOn = this.choosePlanService.boltOns.find(b => b.stripe_id === id);
      return sum + (boltOn ? boltOn.price : 0);
    }, 0);
    return Math.round(planPrice + boltOnsPrice);
  });


  /** Services */
  public choosePlanService = inject(ChoosePlanService);
  public subscriptionService = inject(SubscriptionService);
  private currentUserService = inject(CurrentUserService);
  public toastNotificationService = inject(ToastNotificationService); 
  /** Services END */

  @HostListener('document:keydown.escape')
  onEscape() {
    this.closeModal();
  }

  ngOnInit(): void {
    this.choosePlanService.preSelectedBoltOns().forEach(boltOn => {
      this.subscriptionService.selectedBoltOns.update(current => {
        const newSet = new Set(current);
        newSet.add(boltOn);
        return newSet;
      });
    });
  }


  toggleBoltOn(boltOnStripeId: string) {
    this.subscriptionService.selectedBoltOns.update(current => {
      const newSet = new Set(current);
      if (newSet.has(boltOnStripeId)) {
        newSet.delete(boltOnStripeId);
      } else {
        newSet.add(boltOnStripeId);
      }
      return newSet;
    });
  }

  isBoltOnSelected(boltOnStripeId: string): boolean {
    return this.subscriptionService.selectedBoltOns().has(boltOnStripeId);
  }

  isBoltOnIncluded(_boltOnStripeId: string): boolean {
    return false; // Essential Plan: bolt-ons are always optional paid add-ons
  }

  toggleBoltOnInfo(_boltOnStripeId: string) {
    this.showAllBoltOnDetails.update(v => !v);
  }

  isBoltOnInfoExpanded(_boltOnStripeId: string): boolean {
    return this.showAllBoltOnDetails();
  }

  getBoltOnName(boltOns: Set<string>): string {
    if (boltOns.size === 1) {
      return this.choosePlanService.boltOns.find(b => b.stripe_id === Array.from(boltOns)[0])?.name ?? '';
    } else {
      return 'Multiple Bolt-Ons';
    }
  }

  getBoltOnPrice(boltOns: Set<string>): number {
    return this.choosePlanService.boltOns.find(b => b.stripe_id === Array.from(boltOns)[0])?.price ?? 0;
  }

  getBoltOnCurrency(boltOns: Set<string>): string {
    return 'GBP';
  }

  getBoltOnNameFromId(boltOnId: string): string {
    return this.getBoltOnName(new Set([boltOnId]));
  }

  getBoltOnFeatures(boltOns: Set<string>): string[] {
    return this.choosePlanService.boltOns.find(b => b.stripe_id === Array.from(boltOns)[0])?.mode_details ?? [];
  }

  getBoltOnFeaturesFromId(boltOnId: string): string[] {
    return this.choosePlanService.boltOns.find(b => b.stripe_id === boltOnId)?.mode_details ?? [];
  }

  addBoltOnToPlan() {
    console.log('🤑🤑🤑 addBoltOnToPlan() 🤑🤑🤑');
    console.log('🤑🤑🤑 subscriptionService.subscription() 🤑🤑🤑', this.subscriptionService.subscription());
    this.subscriptionLoadingCtrl.set(true);

    const isAgency = !!this.currentUserService.focussedAgency;

    this.subscriptionService.successUrl.set(isAgency ? `${window.location.protocol}//${window.location.host}/thankyou-agency` : `${window.location.protocol}//${window.location.host}/thankyou-brand`);
    const cancelUrl = isAgency ? `${window.location.protocol}//${window.location.host}/my-agency` : `${window.location.protocol}//${window.location.host}/my-brand`;

    const request: CreateSubSessionMultiProduct = {
      stripeCustomerId: isAgency
        ? this.currentUserService.focussedAgency!.agency.stripeCustomerId
        : this.currentUserService.focussedBrand?.stripeCustomerId,
      products: Array.from(this.subscriptionService.selectedBoltOns()).map(boltOn => ({
        productId: boltOn,
        quantity: 1,
      })),
      successUrl: this.subscriptionService.successUrl() ? this.subscriptionService.successUrl()! : '',
      cancelUrl,
    };

    const customerId = request.stripeCustomerId;
    if (!customerId) {
      this.subscriptionLoadingCtrl.set(false);
      void this.toastNotificationService.alert({
        title: 'Bolt-on failed',
        message: 'Missing billing customer. Please refresh and try again.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    this.subscriptionService.addBoltOnLineItems(request).pipe(
      switchMap((updatedSub) =>
        updatedSub != null
          ? of(updatedSub)
          : this.subscriptionService.getSubscriptionForCustomer(customerId).pipe(take(1)),
      ),
      tap((fresh) => {
        if (fresh) {
          this.subscriptionService.subscription.set(fresh);
          this.currentUserService.setCurrentSubscription(fresh);
        }
      }),
      finalize(() => this.subscriptionLoadingCtrl.set(false)),
    ).subscribe({
      next: () => {
        console.log('🤑🤑🤑 addBoltOnToPlan complete 🤑🤑🤑');
        void this.toastNotificationService.alert({
          title: 'Bolt-on added to plan!',
          message: 'Bolt-on has been added to your plan successfully.',
          type: 'success',
          icon: 'success'
        });
        this.closeModal();
      },
      error: (err) => {
        console.error('addBoltOnToPlan error:', err);
        void this.toastNotificationService.alert({
          title: 'Could not add bolt-on',
          message: 'Something went wrong. Please try again.',
          type: 'error',
          icon: 'error',
        });
      },
    });
  }

  closeModal() {
    this.choosePlanService.closeAllModals();
  }
}
